/**
 * DcYuskarBlock — ход голосования ЮСКАРа в подвкладке «Газета».
 * Расклад по номинациям (счёт, доля, лидер), поимённый список «кто за кого»
 * с временем и пометкой переголосования, динамика по дням.
 */
import { useCallback, useEffect, useState, type CSSProperties, type ReactElement } from "react";
import { api } from "../../api/client";
import { AvatarImg, Bar, NUM, VideoThumb, box, capTitle, fmt, fmtDm } from "./dcVideoStatsParts";
import { DcYuskarPreview, type YuskarPreview } from "./DcYuskarPreview";
import { DcYuskarQueue, type YuskarSubmission } from "./DcYuskarQueue";

interface YuskarCandidate {
  id: string; label: string; votes: number; share: number;
  src: string; poster: string; durationSec: number; bookingId: number; game: string;
}
interface YuskarNomination { id: string; title: string; totalVotes: number; leader: string; candidates: YuskarCandidate[] }
interface YuskarVoter {
  nominationId: string; nomination: string; candidateId: string; candidate: string;
  tg: number; name: string; username: string; avatarUrl: string;
  votedAt: string; changedAt: string; revoted: boolean; identified: boolean;
}
interface YuskarProgress {
  issue: number; votingOpen: boolean;
  summary: { votes: number; voters: number; identified: number; revotes: number; audience: number; turnout: number; submissions: number };
  nominations: YuskarNomination[];
  submissions: YuskarSubmission[];
  voters: YuskarVoter[];
  daily: { d: string; n: number }[];
}

export function DcYuskarBlock({ onImpersonate, from, to }: { onImpersonate?: (tg: number) => void; from?: string; to?: string }): ReactElement {
  const [data, setData] = useState<YuskarProgress | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<"nominations" | "voters">("nominations");
  const [filter, setFilter] = useState<string>("all");
  const [open, setOpen] = useState<YuskarPreview | null>(null);
  const [err, setErr] = useState("");

  // Период — общий календарь вкладки: голоса и заявки режутся по нему на сервере.
  const load = useCallback(() => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (from) qs.set("from", from);
    if (to) qs.set("to", to);
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    api<YuskarProgress>(`/api/commerce/yuskar-progress${suffix}`)
      .then(setData).catch(() => setData(null)).finally(() => setLoading(false));
  }, [from, to]);
  useEffect(() => { load(); }, [load]);

  const seg = (on: boolean): CSSProperties => ({ padding: "3px 8px", borderRadius: 999, fontSize: 11, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap", border: "1px solid " + (on ? "var(--purple)" : "var(--surface-3)"), background: on ? "color-mix(in srgb, var(--purple) 18%, transparent)" : "transparent", color: on ? "var(--text-1)" : "var(--text-2)" });
  const when = (iso: string): string => iso ? `${iso.slice(8, 10)}.${iso.slice(5, 7)} ${iso.slice(11, 16)}` : "—";

  if (loading && !data) return <div style={{ ...box, padding: 12, color: "var(--text-3)", fontSize: 12 }}>Загрузка ЮСКАРа…</div>;
  if (!data) return <div style={{ ...box, padding: 12, color: "var(--text-3)", fontSize: 12 }}>Данные ЮСКАРа недоступны</div>;

  const s = data.summary;
  const dayMax = Math.max(1, ...data.daily.map((d) => d.n));
  const voters = filter === "all" ? data.voters : data.voters.filter((v) => v.nominationId === filter);

  /**
   * Взять присланное игроком видео в номинанты выбранной номинации.
   * Ошибку показываем: раньше её глушил пустой catch, и упавший запрос выглядел
   * так, будто кнопка просто не нажимается.
   */
  const nominate = async (materialId: number, nominationId: string): Promise<void> => {
    try {
      await api<{ ok: true }>("/api/commerce/yuskar-progress/nominate", {
        method: "POST", body: { materialId, nominationId },
      });
      setErr(""); load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Не удалось добавить в номинацию");
      throw e;
    }
  };

  const bindGame = async (candidateId: string, bookingId: number): Promise<void> => {
    try {
      await api<{ ok: true }>("/api/commerce/yuskar-progress/game", {
        method: "PATCH", body: { candidateId, bookingId },
      });
      setErr(""); setOpen(null); load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Не удалось привязать игру");
    }
  };


  return (
    <div style={{ ...box, padding: 8, display: "grid", gap: 6 }}>
      {open ? (
        <DcYuskarPreview preview={open} nominations={data.nominations}
          onClose={() => setOpen(null)}
          onNominate={async (materialId, nominationId) => {
            await nominate(materialId, nominationId);
            setOpen((p) => (p ? { ...p, takenIn: [...(p.takenIn ?? []), nominationId] } : p));
          }}
          onBindGame={bindGame} />
      ) : null}

      {err ? (
        <div role="alert" style={{ padding: "5px 8px", borderRadius: 6, fontSize: 11.5, border: "1px solid var(--red)", color: "var(--red)" }}>{err}</div>
      ) : null}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
        <span style={capTitle}>ЮСКАР · ход голосования</span>
        <span style={{ ...NUM, fontSize: 10, color: data.votingOpen ? "var(--role)" : "var(--text-3)" }}>
          выпуск {data.issue} · {data.votingOpen ? "идёт" : "закрыто"}
        </span>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 6 }}>
        {[
          { l: "Голосов", v: fmt(s.votes), s: `${fmt(s.revotes)} переголос.` },
          { l: "Голосовало", v: fmt(s.voters), s: `${fmt(s.identified)} опознано` },
          { l: "Явка", v: `${fmt(s.turnout)}%`, s: `из ${fmt(s.audience)}` },
          { l: "Номинаций", v: fmt(data.nominations.length), s: "" },
        ].map((k) => (
          <div key={k.l} style={{ background: "var(--surface-2)", borderRadius: 6, padding: "5px 7px" }}>
            <div style={{ ...capTitle, fontSize: 9 }}>{k.l}</div>
            <div style={{ ...NUM, fontSize: 16, fontWeight: 800, color: "var(--text-1)" }}>{k.v}</div>
            {k.s ? <div style={{ ...NUM, fontSize: 9, color: "var(--text-3)" }}>{k.s}</div> : null}
          </div>
        ))}
      </div>

      {data.daily.length > 0 ? (
        <div style={{ display: "grid", gap: 3 }}>
          <span style={{ ...capTitle, fontSize: 9.5 }}>По дням</span>
          {data.daily.map((d) => <Bar key={d.d} label={fmtDm(d.d)} count={d.n} max={dayMax} />)}
        </div>
      ) : null}

      <DcYuskarQueue submissions={data.submissions} nominations={data.nominations} onOpen={setOpen} />

      <div style={{ display: "flex", gap: 4 }}>
        {(["nominations", "voters"] as const).map((t) => (
          <button key={t} type="button" onClick={() => setTab(t)} style={{ ...seg(tab === t), flex: 1 }}>
            {t === "nominations" ? "Расклад" : "Кто за кого"}
          </button>
        ))}
      </div>

      {tab === "nominations" ? (
        data.nominations.map((n) => (
          <div key={n.id} style={{ display: "grid", gap: 3, paddingTop: 4, borderTop: "1px solid var(--surface-3)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
              <span style={{ fontSize: 12.5, color: "var(--text-1)", fontWeight: 700 }}>{n.title}</span>
              <span style={{ ...NUM, fontSize: 10, color: "var(--text-3)" }}>{fmt(n.totalVotes)} голосов</span>
            </div>
            {n.candidates.map((cd, i) => (
              <button key={cd.id} type="button"
                onClick={() => setOpen({ id: cd.id, label: cd.label, src: cd.src, poster: cd.poster, game: cd.game, bookingId: cd.bookingId, votes: cd.votes, share: cd.share, durationSec: cd.durationSec })}
                style={{ display: "grid", gridTemplateColumns: "44px 1fr auto", gap: 8, alignItems: "center", width: "100%", minHeight: 34, padding: "3px 2px", border: "none", background: "transparent", color: "var(--text-1)", cursor: "pointer", textAlign: "left" }}>
                <VideoThumb url={cd.poster} w={44} h={28} />
                <span style={{ minWidth: 0 }}>
                  <span style={{ fontSize: 11.5, display: "block", color: i === 0 && cd.votes > 0 ? "var(--role)" : "var(--text-2)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {i === 0 && cd.votes > 0 ? "★ " : ""}{cd.label}
                  </span>
                  <span style={{ ...NUM, fontSize: 9, color: cd.game ? "var(--text-3)" : "var(--red)" }}>
                    {cd.game || "игра не привязана"}
                  </span>
                </span>
                <span style={{ ...NUM, fontSize: 11.5, color: "var(--text-1)", fontWeight: 700, textAlign: "right" }}>
                  {fmt(cd.votes)} <span style={{ color: "var(--text-3)", fontWeight: 400 }}>· {fmt(cd.share)}%</span>
                </span>
              </button>
            ))}
          </div>
        ))
      ) : (
        <>
          <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
            <button type="button" onClick={() => setFilter("all")} style={seg(filter === "all")}>Все</button>
            {data.nominations.map((n) => (
              <button key={n.id} type="button" onClick={() => setFilter(n.id)} style={seg(filter === n.id)}>{n.title}</button>
            ))}
          </div>
          {voters.length === 0 ? <div style={{ padding: 10, color: "var(--text-3)", fontSize: 12 }}>Голосов пока нет</div>
            : voters.map((v, i) => (
              <button key={`${v.nominationId}-${v.tg}-${i}`} type="button" onClick={() => v.tg && onImpersonate?.(v.tg)}
                style={{ display: "grid", gridTemplateColumns: "24px 1fr auto", gap: 8, alignItems: "center", width: "100%", minHeight: 32, padding: "3px 4px", border: "none", borderTop: i ? "1px solid var(--surface-3)" : "none", background: "transparent", color: "var(--text-1)", cursor: v.tg ? "pointer" : "default", textAlign: "left" }}>
                <AvatarImg url={v.avatarUrl} name={v.name || v.username || "?"} size={24} />
                <span style={{ minWidth: 0 }}>
                  <span style={{ fontSize: 12, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {v.identified ? (v.name || (v.username ? "@" + v.username : "id " + v.tg)) : "неопознанный голос"}
                  </span>
                  <span style={{ ...NUM, fontSize: 9.5, color: "var(--text-3)" }}>
                    {v.nomination} → <b style={{ color: "var(--text-2)" }}>{v.candidate}</b>{v.revoted ? " · переголосовал" : ""}
                  </span>
                </span>
                <span style={{ ...NUM, fontSize: 9.5, color: "var(--text-3)", textAlign: "right" }}>{when(v.changedAt)}</span>
              </button>
            ))}
        </>
      )}
    </div>
  );
}
